import axios from '@/config/axios';

let cachedCategories = null;

async function getAllCategories() {
  if (cachedCategories) return cachedCategories;
  try {
    const res = await axios.get('category/v1/get-all', { _skip403Redirect: true });
    if (res.data && res.data.status === '200' && Array.isArray(res.data.result)) {
      cachedCategories = res.data.result;
      return cachedCategories;
    }
  } catch (e) {
    console.error('Error fetching categories in BookCard:', e);
  }
  return [];
}

export default {
  name: 'BookCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    mode: {
      type: String,
      default: 'catalog' // 'bookcase' (with update/delete footer) or 'catalog'
    },
    defaultCover: {
      type: String,
      default: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=400&q=80"
    }
  },
  emits: ['click', 'update', 'delete', 'view-note'],
  computed: {
    bookId() {
      return this.item.bookId || this.item.id;
    },
    bookName() {
      return this.item.bookName || this.item.name || 'Chưa có tên';
    },
    isOldBook() {
      return this.item.price !== undefined || this.item.verStatus !== undefined || this.item.perStatus !== undefined || this.item.creatorName !== undefined || (this.$route && (this.$route.path.startsWith('/old-books') || this.$route.path.startsWith('/my-old-books')));
    },
    detailRoute() {
      if (this.isOldBook) {
        return '/old-books/' + this.bookId;
      }
      return '/book/' + this.bookId;
    },
    coverUrl() {
      return this.item.cover || this.defaultCover;
    },
    authorsStr() {
      let res = '';
      if (this.item.authorsStr) res = this.item.authorsStr;
      else if (this.item.authorName) res = this.item.authorName;
      else if (this.item.author) res = typeof this.item.author === 'string' ? this.item.author : (this.item.author.name || '');
      else if (Array.isArray(this.item.authors)) {
        res = this.item.authors.map(a => typeof a === 'object' ? (a.name || a.authorName || '') : a).filter(Boolean).join(', ');
      }
      else if (typeof this.item.authors === 'string') res = this.item.authors;

      if (!res) return '';
      res = String(res).trim();
      if (res === '[]' || res === '[""]' || res === '[ ]' || res === 'null') return '';
      return res;
    },
    authorsList() {
      const list = [];
      if (Array.isArray(this.item.authors) && this.item.authors.length > 0) {
        this.item.authors.forEach(a => {
          if (typeof a === 'object' && a !== null) {
            const name = a.name || a.authorName || '';
            if (name) list.push({ id: a.id || null, name });
          } else if (typeof a === 'string' && a.trim()) {
            list.push({ id: null, name: a.trim() });
          }
        });
      } else if (this.item.authorsStr && typeof this.item.authorsStr === 'string' && this.item.authorsStr.trim()) {
        const raw = this.item.authorsStr.trim();
        if (raw !== '[]' && raw !== '[""]' && raw !== '[ ]' && raw !== 'null') {
          if (raw.includes(':')) {
            raw.split(';').forEach(part => {
              const tokens = part.split(':');
              if (tokens.length >= 2 && tokens[1]) {
                list.push({ id: tokens[0], name: tokens[1] });
              }
            });
          } else {
            raw.split(',').forEach(name => {
              if (name.trim()) list.push({ id: null, name: name.trim() });
            });
          }
        }
      } else if (this.item.authorName) {
        list.push({ id: this.item.authorId || null, name: String(this.item.authorName).trim() });
      } else if (this.item.author) {
        if (typeof this.item.author === 'object') {
          list.push({ id: this.item.author.id || null, name: this.item.author.name || '' });
        } else if (typeof this.item.author === 'string' && this.item.author.trim()) {
          this.item.author.split(',').forEach(name => {
            if (name.trim()) list.push({ id: null, name: name.trim() });
          });
        }
      }
      return list.filter(item => item.name);
    },
    hasAuthors() {
      return this.authorsList.length > 0 || !!this.authorsStr;
    },
    creatorName() {
      return this.item.creatorName || this.item.sellerName || '';
    },
    priceFormatted() {
      if (this.item.price !== undefined && this.item.price !== null) {
        if (typeof this.item.price === 'number') {
          if (this.item.price === 0) return 'Miễn phí';
          return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(this.item.price);
        }
        return String(this.item.price);
      }
      return '';
    },
    perStatusText() {
      if (this.item.perStatus !== undefined && this.item.perStatus !== null) {
        return `${this.item.perStatus}% mới`;
      }
      return '';
    },
    verStatusText() {
      if (this.item.verStatus !== undefined && this.item.verStatus !== null) {
        const conditions = {
          0: 'Rất mới',
          1: 'Như mới',
          2: 'Khá mới',
          3: 'Trung bình',
          4: 'Cũ'
        };
        return conditions[this.item.verStatus] || '';
      }
      return '';
    },
    translatorsStr() {
      let res = '';
      if (this.item.translatorsStr) res = this.item.translatorsStr;
      else if (this.item.translatorName) res = this.item.translatorName;
      else if (Array.isArray(this.item.translators)) {
        if (this.item.translators.length === 0) return '';
        res = this.item.translators.map(t => typeof t === 'object' ? (t.name || t.translatorName || '') : t).filter(Boolean).join(', ');
      }
      else if (typeof this.item.translators === 'string') res = this.item.translators;
      else return '';

      res = String(res).trim();
      if (!res || res === '[]' || res === '[""]' || res === '[ ]' || res === 'null') return '';
      if (res.includes(':')) {
        const names = [];
        res.split(';').forEach(part => {
          const p = String(part).trim();
          if (!p) return;
          const name = p.includes(':') ? p.substring(p.indexOf(':') + 1).trim() : p;
          if (name) names.push(name);
        });
        res = names.join(', ');
      }
      return res;
    },
    categoriesStr() {
      let res = '';
      if (this.item.categoriesStr) res = this.item.categoriesStr;
      else if (Array.isArray(this.item.categoryNames) && this.item.categoryNames.length > 0) {
        res = this.item.categoryNames.filter(Boolean).slice(0, 3).join(', ');
      }
      else if (this.item.categoryName) res = this.item.categoryName;
      else if (this.item.category) res = typeof this.item.category === 'string' ? this.item.category : (this.item.category.name || this.item.category.categoryName || '');
      else if (Array.isArray(this.item.categories)) {
        res = this.item.categories.map(c => typeof c === 'object' ? (c.name || c.categoryName || c.title || '') : c).filter(Boolean).slice(0, 3).join(', ');
      }
      else if (Array.isArray(this.item.genres)) {
        res = this.item.genres.map(g => typeof g === 'object' ? (g.name || g.genreName || g.title || '') : g).filter(Boolean).slice(0, 3).join(', ');
      }
      else if (typeof this.item.categories === 'string') res = this.item.categories;

      if (!res) return '';
      res = String(res).trim();
      if (res === '[]' || res === '[""]' || res === '[ ]' || res === 'null') return '';
      return res;
    },
    categoriesList() {
      const list = [];
      if (Array.isArray(this.item.categoryList) && this.item.categoryList.length > 0) {
        this.item.categoryList.forEach(c => {
          if (typeof c === 'object' && c !== null) {
            const name = c.name || c.categoryName || c.title || '';
            if (name) list.push({ id: c.id || null, name });
          } else if (typeof c === 'string' && c.trim()) {
            list.push({ id: null, name: c.trim() });
          }
        });
      } else if (Array.isArray(this.item.categories) && this.item.categories.length > 0) {
        this.item.categories.forEach(c => {
          if (typeof c === 'object' && c !== null) {
            const name = c.name || c.categoryName || c.title || '';
            if (name) list.push({ id: c.id || null, name });
          } else if (typeof c === 'string' && c.trim()) {
            list.push({ id: null, name: c.trim() });
          }
        });
      } else if (Array.isArray(this.item.genres) && this.item.genres.length > 0) {
        this.item.genres.forEach(g => {
          if (typeof g === 'object' && g !== null) {
            const name = g.name || g.genreName || g.title || '';
            if (name) list.push({ id: g.id || null, name });
          } else if (typeof g === 'string' && g.trim()) {
            list.push({ id: null, name: g.trim() });
          }
        });
      } else if (Array.isArray(this.item.categoryNames) && this.item.categoryNames.length > 0) {
        this.item.categoryNames.forEach(n => {
          if (typeof n === 'string' && n.trim()) list.push({ id: null, name: n.trim() });
        });
      } else if (this.item.categoriesStr && typeof this.item.categoriesStr === 'string' && this.item.categoriesStr.trim()) {
        const raw = this.item.categoriesStr.trim();
        if (raw !== '[]' && raw !== '[""]' && raw !== '[ ]' && raw !== 'null') {
          if (raw.includes(':')) {
            raw.split(';').forEach(part => {
              const tokens = part.split(':');
              if (tokens.length >= 2 && tokens[1]) {
                list.push({ id: tokens[0], name: tokens[1] });
              }
            });
          } else {
            raw.split(',').forEach(name => {
              if (name.trim()) list.push({ id: null, name: name.trim() });
            });
          }
        }
      } else if (this.item.categoryName) {
        list.push({ id: this.item.categoryId || null, name: String(this.item.categoryName).trim() });
      } else if (this.item.category) {
        if (typeof this.item.category === 'object') {
          list.push({ id: this.item.category.id || null, name: this.item.category.name || this.item.category.categoryName || '' });
        } else if (typeof this.item.category === 'string' && this.item.category.trim()) {
          list.push({ id: null, name: this.item.category.trim() });
        }
      }
      return list.filter(item => item.name).slice(0, 3);
    },
    hasCategories() {
      return this.categoriesList.length > 0 || !!this.categoriesStr;
    },
    progressStatus() {
      return this.item.progressStatus;
    },
    hasReadingStatus() {
      const s = Number(this.progressStatus);
      return !isNaN(s) && s > 0;
    },
    ownStatus() {
      return this.item.ownStatus;
    },
    hasOwnership() {
      const o = Number(this.ownStatus);
      return !isNaN(o) && o > 0;
    },
    myEvaluate() {
      if (this.item.myEvaluate !== undefined && this.item.myEvaluate !== null && Number(this.item.myEvaluate) > 0) {
        return Number(this.item.myEvaluate);
      }
      if (this.item.userRating !== undefined && this.item.userRating !== null && Number(this.item.userRating) > 0) {
        return Number(this.item.userRating);
      }
      return 0;
    },
    communityEvaluate() {
      const val = this.item.evaluate ?? this.item.rating ?? this.item.score;
      if (val !== undefined && val !== null && !isNaN(Number(val))) {
        return Number(val);
      }
      return 0;
    },
    isHot() {
      if (this.item.isHot !== undefined) return !!this.item.isHot;
      return this.communityEvaluate >= 4.5;
    },
    note() {
      return this.item.note || '';
    },
    hasProgress() {
      return (
        this.progressStatus !== undefined &&
        this.progressStatus !== null &&
        (Number(this.progressStatus) === 2 || Number(this.progressStatus) === 3 || Number(this.progressStatus) === 4 || this.item.percent !== undefined)
      );
    },
    isBookcaseMode() {
      return this.mode === 'bookcase';
    }
  },
  methods: {
    onImageError(e) {
      if (e && e.target) {
        e.target.src = this.defaultCover;
      }
    },
    getStatusLabel(status) {
      switch (Number(status)) {
        case 1: return "Muốn đọc";
        case 2: return "Đang đọc";
        case 3: return "Đã đọc xong";
        case 4: return "Tạm ngưng";
        default: return "";
      }
    },
    getStatusBadgeClass(status) {
      switch (Number(status)) {
        case 1: return "badge-status-want";
        case 2: return "badge-status-reading";
        case 3: return "badge-status-completed";
        case 4: return "badge-status-paused";
        default: return "badge-status-unread";
      }
    },
    getOwnStatusLabel(status) {
      switch (Number(status)) {
        case 1: return "Sách giấy";
        case 2: return "Ebook";
        default: return "";
      }
    },
    getOwnStatusIcon(status) {
      switch (Number(status)) {
        case 1: return "fa-solid fa-book";
        case 2: return "fa-solid fa-tablet-screen-button";
        default: return "fa-solid fa-xmark";
      }
    },
    getOwnStatusBadgeClass(status) {
      switch (Number(status)) {
        case 1: return "badge-own-paper";
        case 2: return "badge-own-ebook";
        default: return "badge-own-none";
      }
    },
    getProgressBarClass(status) {
      switch (Number(status)) {
        case 2: return "bg-success-gradient";
        case 3: return "bg-purple-gradient";
        case 4: return "bg-warning-gradient";
        default: return "bg-primary-gradient";
      }
    },
    getProgressTextClass(status) {
      switch (Number(status)) {
        case 3: return "text-completed";
        default: return "";
      }
    },
    calculateVisualPercent() {
      const s = Number(this.progressStatus);
      if (s === 3) return 100;
      if (s === 0 || s === 1) return 0;
      if (this.item.type) {
        return Math.min(100, Math.max(0, this.item.percent || 0));
      }
      if (this.item.totalPage > 0 && this.item.currentPage >= 0) {
        return Math.min(100, Math.max(0, Math.round((this.item.currentPage / this.item.totalPage) * 100)));
      }
      return this.item.percent || 0;
    },
    formatProgressText() {
      const s = Number(this.progressStatus);
      if (s === 3) return "100% (Đã đọc xong)";
      if (s === 0) return "0% (Chưa đọc)";
      if (s === 1) return "0% (Muốn đọc)";

      if (this.item.type) {
        return `${(this.item.percent || 0).toFixed(0)}%`;
      }
      if (this.item.totalPage > 0) {
        const pct = Math.round(((this.item.currentPage || 0) / this.item.totalPage) * 100);
        return `Trang ${this.item.currentPage || 0}/${this.item.totalPage} (${pct}%)`;
      }
      return `${(this.item.percent || 0).toFixed(0)}%`;
    },
    isValidNote(note) {
      if (!note) return false;
      const trimmed = note.trim();
      return (
        trimmed !== "" &&
        trimmed !== "." &&
        trimmed !== "-" &&
        !trimmed.startsWith("Cập nhật trạng thái:")
      );
    },
    isLongNoteGrid(note) {
      return this.isValidNote(note) && note.trim().length > 15;
    },
    onCardClick() {
      this.$emit('click', this.item);
    },
    goSearch(text) {
      const key = (text || '').trim();
      if (!key) return;
      window.currentSearchKey = key;
      sessionStorage.setItem('lastSearchKey', key);
      sessionStorage.setItem('lastSearchPage', '0');
      const event = new CustomEvent('search-key-sync', { detail: key });
      window.dispatchEvent(event);
      if (this.$route && this.$route.path === '/search') {
        window.history.replaceState({ ...window.history.state, key, page: 0 }, '');
        const routeEvent = new CustomEvent('search-triggered', { detail: { key, page: 0 } });
        window.dispatchEvent(routeEvent);
      } else {
        this.$router.push({
          path: '/search',
          state: { key, page: 0 }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err);
          }
        });
      }
    },
    async goSearchAuthor(authorObj) {
      if (!authorObj) return;
      const authorId = typeof authorObj === 'object' ? (authorObj.id || authorObj.authorId) : null;
      const authorName = typeof authorObj === 'object' ? (authorObj.name || authorObj.authorName || '') : String(authorObj);
      const cleanName = authorName ? authorName.trim() : '';
      if (!cleanName) return;

      // If this is an Old Book or we are in the Old Books section, search only within Old Books
      if (this.isOldBook) {
        this.$router.push({
          path: '/old-books',
          query: { keyword: cleanName }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
        return;
      }

      if (authorId) {
        this.$router.push({
          path: `/author/${authorId}`,
          state: { authorName: cleanName }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
        return;
      }

      try {
        const res = await axios.get('author/v1/search', {
          params: { key: cleanName, page: 0 },
          _skip403Redirect: true
        });
        let list = [];
        if (res.data && res.data.status === '200' && res.data.result) {
          if (Array.isArray(res.data.result.content)) {
            list = res.data.result.content;
          } else if (Array.isArray(res.data.result)) {
            list = res.data.result;
          }
        }
        if (list.length > 0) {
          const match = list.find(a => a.name && a.name.trim().toLowerCase() === cleanName.toLowerCase()) || list[0];
          if (match && match.id) {
            this.$router.push({
              path: `/author/${match.id}`,
              state: { authorName: match.name || cleanName }
            }).catch(err => {
              if (err.name !== 'NavigationDuplicated') console.error(err);
            });
            return;
          }
        }
      } catch (e) {
        console.error('Error finding author by name:', e);
      }

      this.goSearch(cleanName);
    },
    async goSearchCategory(catObj) {
      if (!catObj) return;
      const catId = typeof catObj === 'object' ? (catObj.id || catObj.categoryId) : null;
      const catName = typeof catObj === 'object' ? (catObj.name || catObj.categoryName || '') : String(catObj);
      const cleanName = catName ? catName.trim() : '';
      if (!cleanName) return;

      // If this is an Old Book or we are in the Old Books section, search only within Old Books
      if (this.isOldBook) {
        this.$router.push({
          path: '/old-books',
          query: { keyword: cleanName }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
        return;
      }

      if (catId) {
        this.$router.push({
          path: `/category/${catId}`,
          state: { categoryName: cleanName }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
        return;
      }

      try {
        const categories = await getAllCategories();
        if (categories && categories.length > 0) {
          const match = categories.find(c => c.name && c.name.trim().toLowerCase() === cleanName.toLowerCase()) ||
                        categories.find(c => c.name && c.name.trim().toLowerCase().includes(cleanName.toLowerCase()));
          if (match && match.id) {
            this.$router.push({
              path: `/category/${match.id}`,
              state: { categoryName: match.name || cleanName }
            }).catch(err => {
              if (err.name !== 'NavigationDuplicated') console.error(err);
            });
            return;
          }
        }
      } catch (e) {
        console.error('Error finding category by name:', e);
      }

      this.goSearch(cleanName);
    },
    onUpdateClick() {
      this.$emit('update', this.item);
    },
    onDeleteClick() {
      this.$emit('delete', this.item);
    },
    onViewNoteClick() {
      this.$emit('view-note', this.item);
    }
  }
};
